import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ordersService } from '../../../services/orders.service';
import { EmsServicesService } from 'src/app/module/ftl-master/services/ems-services.service';
import { combineLatest, debounceTime, distinctUntilChanged, startWith, Subject } from 'rxjs';
import * as moment from 'moment';

interface DropdownOption {
  id: any;
  vehicleType: string;
}

interface OrderTypeOption {
  value: 'Contract' | 'Spot';
  label: string;
  description: string;
  icon: string;
}

interface RateTypeOption {
  value: 'RATE_KNOWN' | 'NULL_PRICE';
  label: string;
}

@Component({
  selector: 'app-create-tms-order',
  templateUrl: './create-tms-order.component.html',
  styleUrls: ['./create-tms-order.component.css']
})
export class CreateTmsOrderComponent {


  theme = 'dark';

  orderForm!: FormGroup;

  customers: DropdownOption[] = [];
  kamList: DropdownOption[] = [];
  procurementExecList: DropdownOption[] = [];
  allRoles:any;

  vehicleTypeList: DropdownOption[] = [];
  contractList:any;
  orderTypes: OrderTypeOption[] = [
    {
      value: 'Contract',
      label: 'Contract',
      description: 'Rate auto-fetched from Contract Master',
      icon: 'file-text'
    },
    {
      value: 'Spot',
      label: 'Spot',
      description: 'Rate negotiated – KAM & Procurement workflow',
      icon: 'zap'
    }
  ];

  customerRateTypes: RateTypeOption[] = [
    { value: 'RATE_KNOWN', label: 'Rate Known' },
    { value: 'NULL_PRICE', label: 'Without Price' }
  ];

  routes: any;
  locationList: any;
  accounts:any;
  UserDetails:any;
  userRole:any;
  userType:any;
  accountIds:any;
  filters: any;

  accountList: any[] = [];
  accountPage = 0;
  accountSize = 50;
  accountSearchText = '';
  isLoadingAccounts = false;
  hasMoreAccounts = true;
  accountSearch$ = new Subject<string>();
  selectedRouteSourceId: any = null;
  selectedRouteDestinationId: any = null;
  minPlacementDate: Date = this.calculateMinDate();
  roleNames: string[] = [];
  isEdit=false
  editData:any;
  constructor(
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private orderService: ordersService,
    private emsServices: EmsServicesService
  ) {
    this.accounts = JSON.parse(localStorage.getItem('accountList')!)
    this.UserDetails = JSON.parse(localStorage.getItem('UserDetails')!);
    this.roleNames = (this.UserDetails?.userRoles || []).map((r: any) => r.name);
    this.userType = this.UserDetails?.userType;
    this.accountIds = Array.from(new Set(this.UserDetails.accountMapping.map((m:any) => m.accountId)));
  }

  ngOnInit(): void {
    this.accountSearch$
    .pipe(
      debounceTime(500), // wait 500ms after typing stops
      distinctUntilChanged()
    )
    .subscribe((searchText: string) => {
      this.accountSearchText = searchText;
      this.getAccounts(true);
    });

    setInterval(() => {
      this.minPlacementDate = this.calculateMinDate();

      // Re-validate in case the previously selected value is now stale
      const ctrl = this.orderForm.get('placementDateTime');
      if (ctrl?.value) {
        ctrl.updateValueAndValidity({ onlySelf: true });
      }
    }, 60000);

    this.getRoles()
    this.getCustomers();
    this.getVehicleType();
    this.getLocations()
    this.getAccounts(true);
    this.buildForm();
    this.routeDetails.get('routeId')?.valueChanges.subscribe((routeId: any) => {
      this.onRouteChange(routeId);
    });
    this.setupHistoricalRateWatcher();
    this.loadDropdownData();

    // Re-evaluate pricing validators whenever order type or rate type changes
    this.orderForm.get('orderType')?.valueChanges.subscribe(() => {
      this.updatePricingValidators();
    });

    this.orderForm.get('customerRateType')?.valueChanges.subscribe(() => {
      this.updatePricingValidators();
    });

    // If opened in "edit" mode, patch existing data
    this.isEdit = false
    this.editData = this.config?.data?.editData?.[0];
    if (this.config?.data?.edit && this.editData) {
      this.isEdit = true
      this.patchForm(this.editData);
    }
  }

  hasRole(...roles: string[]): boolean {
    return roles.some(r => this.roleNames.includes(r));
  }
  notRole(...roles: string[]): boolean {
    return !this.hasRole(...roles);
  }

  private calculateMinDate(): Date {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setSeconds(0, 0);
    return now;
  }

  private minDateTimeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const selected = new Date(control.value);
      const minAllowed = this.calculateMinDate();

      return selected < minAllowed ? { minDateTime: true } : null;
    };
  }

  get minPlacementDateTime(): string {
    const now = new Date();
    // Add 1 hour to current time
    now.setHours(now.getHours() + 1);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const yyyy = now.getFullYear();
    const mm   = pad(now.getMonth() + 1);
    const dd   = pad(now.getDate());
    const hh   = pad(now.getHours());
    const min  = pad(now.getMinutes());

    // Format required by datetime-local: YYYY-MM-DDTHH:MM
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  getCustomers(){
  this.emsServices.getB2bCustomers([], 0, 100000,'Id','ASC').subscribe((data: any) => {
      this.customers = data?.responseObject?.content;
    });
  }

  getRoles(){
    this.emsServices.getRoles().subscribe((res:any)=>{
      this.allRoles = res?.responseObject;
      this.allRoles.forEach((el:any)=>{
        if(el.name === 'ROLE_KAM'){
          this.getKam(el.id)
        }
        if(el.name === 'ROLE_PROCUREMENT'){
          this.getProcurement(el.id)
        }
      })
    })
  }

  getKam(id:any){
    const postData = [
      {
        "key": "ROLE_ID_IN",
        "value": id.toString()
      }
    ]
    this.emsServices.getUsers(postData,0,100000,'id','DESC').subscribe((res:any)=>{
      this.kamList = res?.responseObject?.content;
      this.kamList.forEach((el:any)=>{
        if(el.id === this.UserDetails.id){
          this.orderForm?.controls['assignedKam'].setValue(el.id)
        }
      })
    })
  }

  getProcurement(id:any){
    const postData = [
      {
        "key": "ROLE_ID_IN",
        "value": id.toString()
      }
    ]
    this.emsServices.getUsers(postData,0,100000,'id','DESC').subscribe((res:any)=>{
      this.procurementExecList = res?.responseObject?.content;
      this.orderForm?.controls['assignedKam'].setValue(this.UserDetails.id)
    })
  }

  getVehicleType(){
    this.emsServices.getVehicleType([],0,100000).subscribe((res:any)=>{
      this.vehicleTypeList = res?.responseObject?.content;
    })
  }

  customerChange(event:any){
    if(event){
      this.getCustomerContracts(event.id)
    }
  }

  getCustomerContracts(id:any){
    const postData = [
      {
        "key": "CUSTOMER_ID",
        "value": id.toString()
      }
    ]
    this.emsServices.getCustomerContracts(postData,0,100000).subscribe((res:any)=>{
      this.contractList = res?.responseObject?.content || [];

      this.routes = this.contractList.map((item: any) => ({
        id: item.routeId,
        name: `${item.sourceCity} - ${item.destinationCity}`,
        agreedCost: item.agreedCost
      }));
    })
  }

  onRouteChange(routeId: any): void {
    const selectedRoute = this.contractList.find(
      (x: any) => x.routeId == routeId
    );

    if (selectedRoute) {
      this.routeDetails.patchValue({
        price: selectedRoute.agreedCost,
        vehicleType: selectedRoute.vehicleTypeId
      });

      // Store sourceId and destinationId from selected contract route
      this.selectedRouteSourceId = selectedRoute.sourceId;
      this.selectedRouteDestinationId = selectedRoute.destinationId;
    }
  }

  getAccounts(reset: boolean = false) {
    if (this.isLoadingAccounts) {
      return;
    }

    if (reset) {
      this.accountPage = 0;
      this.accountList = [];
      this.hasMoreAccounts = true;
    }

    if (!this.hasMoreAccounts) {
      return;
    }

    let filters: any[] = [];

    if (this.notRole('ROLE_SUPERUSER') && this.accountIds?.length > 0) {
      filters.push({
        key: 'ACCOUNT_ID_IN',
        value: this.accountIds.join(',')
      });
    }
    if (this.accountSearchText?.trim()) {
      filters.push({
        key: 'NAME_IN',
        value: this.accountSearchText.trim()
      });
    }
    this.isLoadingAccounts = true;
    this.emsServices
      .getAccounts(
        filters,
        this.accountPage,
        this.accountSize,
        'id',
        'DESC'
      )
      .subscribe({
        next: (data: any) => {
          const content = data?.responseObject?.content || [];
          this.accountList = [...this.accountList, ...content];
          this.hasMoreAccounts = content.length === this.accountSize;
          if (content.length > 0) {
            this.accountPage++;
          }
          if(this.accountList.length === 1){
            this.orderForm.controls['accountId'].setValue(this.accountList[0].id)
          }
          this.isLoadingAccounts = false;
        },
        error: () => {
          this.isLoadingAccounts = false;
        }
      });
  }

  onAccountSearch(event: any) {
    this.accountSearch$.next(event.term || '');
  }
  loadMoreAccounts() {
    this.getAccounts(false);
  }
  getLocations() {
    this.emsServices.getLocations([], 0, 100000).subscribe((res: any) => {

      this.locationList = res?.responseObject?.content || [];

      this.locationList.unshift({
        id: -1,
        cityName: 'Others'
      });

    });
  }
  // ---------- Form setup ----------

  buildForm(): void {
    this.orderForm = this.fb.group({
      customer: [null, Validators.required],
      accountId: [null],
      // vehiclePlacementDate: [null, Validators.required],
      assignedKam: [null],
      assignedProcurementExec: [null],
      placementDateTime: [null, [Validators.required, this.minDateTimeValidator()]],
      orderType: ['Spot', Validators.required],
      routeDetails: this.createRouteDetail(),
      shipmentLineItems: this.fb.array([this.createShipmentLineItem()]),
      // Pickup / Drop locations are common to BOTH Spot and Contract orders now,
      // so they live at the top level of the form instead of inside routeDetails.
      pickupLocations: this.fb.array([this.createLocation()]),
      dropLocations: this.fb.array([this.createLocation()]),
      customerRateType: ['RATE_KNOWN'],
      customerRate: [null],
      historicalRate: [null]
    });

    this.updatePricingValidators();
  }

  asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  // ---------- Order type / pricing ----------

  selectOrderType(type: string): void {
    this.orderForm.get('orderType')?.setValue(type);
  }

  selectCustomerRateType(type: string): void {
    if(type === 'NULL_PRICE'){
      this.orderForm.controls['customerRate'].reset()
    }
    this.orderForm.get('customerRateType')?.setValue(type);
  }

  updatePricingValidators(): void {
    const orderType = this.orderForm.get('orderType')?.value;
    const rateType = this.orderForm.get('customerRateType')?.value;
    const rateControl = this.orderForm.get('customerRate');
  
    if (orderType === 'Spot' && rateType === 'RATE_KNOWN') {
      rateControl?.setValidators([Validators.required, Validators.min(0)]);
    } else {
      rateControl?.clearValidators();
    }
    rateControl?.updateValueAndValidity();
  
    const sourceId = this.shipmentLineItems.at(0)?.get('sourceId');
    const destinationId = this.shipmentLineItems.at(0)?.get('destinationId');
    const lineItemVehicleType = this.shipmentLineItems.at(0)?.get('vehicleType'); // ← new
    const routeVehicleType = this.routeDetails.get('vehicleType');   
  
    if (orderType === 'Contract') {
      sourceId?.clearValidators();
      destinationId?.clearValidators();
      lineItemVehicleType?.clearValidators();              
      routeVehicleType?.setValidators([Validators.required]);        
    } else {
      sourceId?.setValidators([Validators.required]);
      destinationId?.setValidators([Validators.required]);
      lineItemVehicleType?.setValidators([Validators.required]); 
      routeVehicleType?.clearValidators();                          
    }
  
    sourceId?.updateValueAndValidity();
    destinationId?.updateValueAndValidity();
    lineItemVehicleType?.updateValueAndValidity();          
    routeVehicleType?.updateValueAndValidity();
}

  isGroupInvalid(group: AbstractControl | null, controlName: string): boolean {
    const control = group?.get(controlName);
    return !!control && control.touched && control.invalid;
  }

  // ---------- Validation helpers ----------

  isInvalid(controlName: string): boolean {
    const control = this.orderForm.get(controlName);
    return !!control && control.touched && control.invalid;
  }

  createLocation(): FormGroup {
    return this.fb.group({
      pincode: [''],
      city: [null],
      otherCity: [''],
      address: ['']
    });
  }

  createShipmentLineItem(): FormGroup {
    return this.fb.group({
      sourceId: [null, Validators.required],
      originAddress: [''],

      destinationId: [null, Validators.required],
      destinationAddress: [''],

      vehicleType: [null, Validators.required],
      trucks: [1]
    });
  }

  get shipmentLineItems(): FormArray {
    return this.orderForm.get('shipmentLineItems') as FormArray;
  }

  addLineItem(): void {
    this.shipmentLineItems.push(this.createShipmentLineItem());
  }

  removeLineItem(index: number): void {
    if (this.shipmentLineItems.length > 1) {
      this.shipmentLineItems.removeAt(index);
    }
  }

  createRouteDetail(): FormGroup {
    return this.fb.group({
      routeId: [null],
      vehicleType: [null,Validators.required],
      noOfVehicle: [1, [Validators.required, Validators.min(1)]],
      price: [null]
    });
  }
  get routeDetails(): FormGroup {
    return this.orderForm.get('routeDetails') as FormGroup;
  }

  // ─────────────────────────────────────────────
  // Pickup / Drop Locations — kept as two independent FormArrays
  // (payload building via buildOrderPoints() is unchanged), but the
  // UI now only exposes ONE "+ Add Location" / remove action that
  // keeps both arrays in lockstep by index — 1 pickup + 1 drop per
  // "Location" card, added/removed together as a pair.
  // ─────────────────────────────────────────────

  get pickupLocations(): FormArray {
    return this.orderForm.get('pickupLocations') as FormArray;
  }

  get dropLocations(): FormArray {
    return this.orderForm.get('dropLocations') as FormArray;
  }

  // Adds one Pickup row + one Drop row together, as a pair.
  addLocationRow(): void {
    this.pickupLocations.push(this.createLocation());
    this.dropLocations.push(this.createLocation());
  }

  // Removes the Pickup + Drop row at the same index together, as a pair.
  removeLocationRow(index: number): void {
    if (this.pickupLocations.length > 1) {
      this.pickupLocations.removeAt(index);
    }
    if (this.dropLocations.length > 1) {
      this.dropLocations.removeAt(index);
    }
  }

  // ---------- Data loading ----------

  loadDropdownData(): void {
    const accounts = JSON.parse(localStorage.getItem('accountList') || '[]');
    this.customers = (accounts || []).map((acc: any) => ({
      id: acc.id,
      name: acc.accountName
    }));

    // TODO: replace with real services for KAM / Procurement Exec lists
  }

  // Rebuilds a pickup/drop FormArray from saved orderPoints of a given pointType (used on edit)
  private patchLocationArray(formArray: FormArray, orderPoints: any[], pointType: 'PICKUP' | 'DROP'): void {
    const filtered = (orderPoints || []).filter((p: any) => p.pointType === pointType);

    if (!filtered.length) {
      return;
    }

    // clear the default empty row, then rebuild from saved data
    while (formArray.length) {
      formArray.removeAt(0);
    }

    filtered.forEach((point: any) => {
      formArray.push(this.fb.group({
        pincode: [point.pincode || ''],
        city: [point.locationId ?? null],
        otherCity: [''],
        address: [point.address || '']
      }));
    });
  }

  patchForm(data: any): void {
    this.orderForm.patchValue({
      customer: data.customerId,
      accountId: data.accountId,
      assignedKam: data.assignedKamId,
      assignedProcurementExec: data.assignedProcurementId,
      orderType: data.orderType ? (data.orderType.charAt(0).toUpperCase() + data.orderType.slice(1).toLowerCase()) : 'Spot',
      customerRateType: data.isNullPrice ? 'NULL_PRICE' : 'RATE_KNOWN',
      customerRate: data.customerAmount,
      historicalRate: data.historicalRate
    });

    // Patch shipment line items for Spot
    if (data.orderType?.toLowerCase() === 'spot') {
      this.shipmentLineItems.at(0).patchValue({
        sourceId: data.sourceId,
        originAddress: data.originAddress || '',
        destinationId: data.destinationId,
        destinationAddress: data.destinationAddress || '',
        vehicleType: data.vehicleTypeId,
        trucks: data.numberOfVehicle || 1
      });
    }

    // Patch route details for Contract
    if (data.orderType?.toLowerCase() === 'contract') {
      this.routeDetails.patchValue({
        routeId: data.routeId,
        vehicleType: data.vehicleTypeId,
        noOfVehicle: data.numberOfVehicle || 1,
        price: data.customerAmount
      });
    }

    // Pickup / drop points are common to both order types now
    if (data.orderPoints?.length) {
      this.patchLocationArray(this.pickupLocations, data.orderPoints, 'PICKUP');
      this.patchLocationArray(this.dropLocations, data.orderPoints, 'DROP');
    }
    
    this.orderForm.patchValue({
      placementDateTime: data.vehicleRequiredAt ? moment(data.vehicleRequiredAt, "DD-MM-YYYY HH:mm:ss Z").toDate() : null,
    })
  }

  // ---------- Actions ----------

  onCancel(): void {
    this.ref.close();
  }

  getInvalidControls(formGroup: FormGroup | FormArray, path: string = ''): string[] {
    const invalidControls: string[] = [];

    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      const controlPath = path ? `${path}.${key}` : key;

      if (control instanceof FormGroup || control instanceof FormArray) {
        invalidControls.push(...this.getInvalidControls(control, controlPath));
      } else if (control && control.invalid) {
        invalidControls.push(controlPath);
      }
    });

    return invalidControls;
  }

  // A pickup/drop row only counts as "filled" if the user actually entered something in it.
  // Empty rows (e.g. the default blank row nobody touched) are skipped entirely.
  private isLocationEntryFilled(loc: any): boolean {
    return !!(
      (loc?.pincode && loc.pincode.toString().trim()) ||
      (loc?.city !== null && loc?.city !== undefined) ||
      (loc?.otherCity && loc.otherCity.toString().trim()) ||
      (loc?.address && loc.address.toString().trim())
    );
  }

  // Builds orderPoints entries for a given pointType, skipping unfilled rows.
  // NOTE: previously this read `pickup.locationId`, but the actual control name
  // on the location FormGroup is `city` - fixed here so locationId is sent correctly.
  private buildOrderPoints(pointType: 'PICKUP' | 'DROP', locations: any[]): any[] {
    return (locations || [])
      .filter((loc: any) => this.isLocationEntryFilled(loc))
      .map((loc: any) => ({
        pointType,
        address: loc.address || '',
        locationId: loc.city === -1 ? null : loc.city,
        pincode: loc.pincode || ''
      }));
  }

  onSubmit(status:any): void {

    if (this.orderForm.invalid) {
      this.orderForm.markAllAsTouched();
      console.log(
        'Invalid Controls:',
        this.getInvalidControls(this.orderForm)
      );
      return;
    }

    const formValue = this.orderForm.value;

    const payload: any = {
      accountId: formValue.accountId,
      customerId: formValue.customer,

      routeId:
        formValue.orderType === 'Contract'
          ? formValue.routeDetails?.routeId
          : null,
      // contractId:formValue.orderType === 'Contract'
      // ? formValue.routeDetails?.id
      // : null,
      vehicleTypeId:
        formValue.orderType === 'Contract'
          ? formValue.routeDetails?.vehicleType
          : formValue.shipmentLineItems?.[0]?.vehicleType,

      assignedKamId: formValue.assignedKam,
      assignedProcurementId: formValue.assignedProcurementExec,

      vehicleRequiredAt: moment(formValue.placementDateTime).format('DD-MM-yyyy hh:mm:ss') + ' ' +'+0530',

      orderType: formValue.orderType?.toLowerCase(),

      customerAmount:
        formValue.orderType === 'Contract'
          ? formValue.routeDetails?.price
          : formValue.customerRate,

      // confirmedAmount:
      //   formValue.orderType === 'Contract'
      //     ? formValue.routeDetails?.price
      //     : formValue.customerRate,

      numberOfVehicle:
        formValue.orderType === 'Contract'
          ? formValue.routeDetails?.noOfVehicle
          : formValue.shipmentLineItems?.[0]?.trucks,

      isNullPrice:
        formValue.customerRateType === 'NULL_PRICE',
      status: status === 'DRAFT' ? 'DRAFT' : formValue.customerRateType === 'NULL_PRICE' ? 'QUOTE_PENDING' : 'CREATED',

      // Common to both Spot & Contract - only rows the user actually filled in are sent
      orderPoints: [
        ...this.buildOrderPoints('PICKUP', formValue.pickupLocations),
        ...this.buildOrderPoints('DROP', formValue.dropLocations)
      ],

      tripStatus: "PENDING",
      lhcStatus: "PENDING",
      softPodStatus: "PENDING",
      hardPodStatus: "PENDING",
      advancePaymentStatus: "PENDING",
      balancePaymentStatus: "PENDING",
      biddingEnable: 0
    };

    // EDIT CASE
    if (this.config?.data?.edit) {
      payload.id = this.config?.data?.editData?.[0]?.id;
    }

    // CONTRACT
    if (formValue.orderType === 'Contract') {
      payload.sourceId = this.selectedRouteSourceId;
      payload.destinationId = this.selectedRouteDestinationId;
    }

    // SPOT
    else {
      const spotItem = formValue.shipmentLineItems?.[0];

      payload.sourceId = spotItem?.sourceId === -1 ? null : spotItem?.sourceId;
      payload.destinationId = spotItem?.destinationId === -1 ? null : spotItem?.destinationId;
      payload.originAddress = spotItem?.originAddress || '';
      payload.destinationAddress = spotItem?.destinationAddress || '';
    }

    console.log(payload);

    this.orderService.createUpdateOrder(payload).subscribe((res:any)=>{
      this.ref.close(res)
    })
  }
  private setupHistoricalRateWatcher(): void {
    const sourceCtrl = this.shipmentLineItems.at(0).get('sourceId');
    const destinationCtrl = this.shipmentLineItems.at(0).get('destinationId');
    const accountCtrl = this.orderForm.get('accountId');
    const orderTypeCtrl = this.orderForm.get('orderType');
  
    combineLatest([
      sourceCtrl!.valueChanges.pipe(startWith(sourceCtrl!.value)),
      destinationCtrl!.valueChanges.pipe(startWith(destinationCtrl!.value)),
      accountCtrl!.valueChanges.pipe(startWith(accountCtrl!.value)),
      orderTypeCtrl!.valueChanges.pipe(startWith(orderTypeCtrl!.value))
    ])
      .pipe(debounceTime(200), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(([sourceId, destinationId, accountId, orderType]) => {
        this.maybeFetchHistoricalRate(sourceId, destinationId, accountId, orderType);
      });
  }
  
  private maybeFetchHistoricalRate(sourceId: any, destinationId: any, accountId: any, orderType: any): void {
    const historicalRateCtrl = this.orderForm.get('historicalRate');
  
    // Historical rate only applies to Spot orders with real (not "Others") locations
    if (
      orderType !== 'Spot' ||
      !sourceId || sourceId === -1 ||
      !destinationId || destinationId === -1 ||
      !accountId
    ) {
      historicalRateCtrl?.setValue(null);
      return;
    }
  
    this.orderService.getHistoricalRate(sourceId, destinationId, accountId).subscribe({
      next: (res: any) => {
        const rate = res?.responseObject?.averageRate ?? res?.responseObject ?? null;
        historicalRateCtrl?.setValue(rate);
      },
      error: () => {
        historicalRateCtrl?.setValue(null);
      }
    });
  }
}